import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { t } from 'i18next';
import { toast } from 'sonner';
import { RadixButton, RadixDialog } from '@/components/radix';
import { useMediaQuery } from '@/hooks/shared';
import type {
    GetApiDataProcessingAgreementsPending200DataItem,
    GetApiDataProcessingAgreementsPending200DataItemAttachmentsAnyOfItem,
    PostApiDataProcessingAgreementIdAcceptBody,
} from '@/shared/api/models';
import { api } from '@/utils/Api/POS';
import { useSelector } from 'react-redux';

const LOCAL_STORAGE_KEY = 'DPA_SHOWN';

type ShownRecords = Record<string, string>;

type DpaButtonAction = 'close' | 'accept';

type DpaButton = {
    label: string;
    action: DpaButtonAction;
};

type DpaButtonLayout = 'DOUBLE' | 'SINGLE';

type DpaAdditionalData = {
    buttons?: DpaButton[];
    buttonLayout?: DpaButtonLayout;
};

type PendingAgreement = GetApiDataProcessingAgreementsPending200DataItem & {
    additionalData?: DpaAdditionalData;
};

const readShownRecords = (): ShownRecords => {
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed as ShownRecords;
        }
        return {};
    } catch {
        return {};
    }
};

const writeShownRecord = (id: string) => {
    const records = readShownRecords();
    records[id] = moment().format('YYYY-MM-DD');
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(records));
};

const wasShownToday = (id: string): boolean => {
    const today = moment().format('YYYY-MM-DD');
    return readShownRecords()[id] === today;
};

const isStartDateEligible = (startDate: PendingAgreement['startDate']): boolean => {
    if (startDate == null || startDate === '') return true;
    const parsed = moment(startDate);
    if (!parsed.isValid()) return false;
    return parsed.startOf('day').isSameOrBefore(moment().startOf('day'));
};

const shouldShowAgreement = (agreement: PendingAgreement): boolean => {
    if (!isStartDateEligible(agreement.startDate)) return false;
    if (agreement.isOneTime) return true;
    return !wasShownToday(agreement.id);
};

const normalizeButtonAction = (action: string): DpaButtonAction => (action === 'accept' ? 'accept' : 'close');

const getDialogButtons = (agreement: PendingAgreement): DpaButton[] => {
    const configured = agreement.additionalData?.buttons;
    if (configured && configured.length > 0) {
        return configured.map((button) => ({
            label: button.label,
            action: normalizeButtonAction(button.action),
        }));
    }

    return [
        { label: t('Common.Dismiss'), action: 'close' },
        { label: t('Calendar.Accept'), action: 'accept' },
    ];
};

const buildAcceptPayload = (agreement: PendingAgreement): PostApiDataProcessingAgreementIdAcceptBody => {
    const buttons = getDialogButtons(agreement);
    const attachments = Array.isArray(agreement.attachments) ? agreement.attachments : [];

    return {
        meta: {
            showed_content: agreement.content,
            showed_title: agreement.title,
            showed_button_labels: buttons.map((button) => button.label),
            attachments: attachments.map((attachment) => ({
                type: attachment.type,
                url: attachment.url,
                fileName: attachment.fileName ?? '',
            })),
        },
    };
};

const getAttachmentHrefAndLabel = (
    attachment: GetApiDataProcessingAgreementsPending200DataItemAttachmentsAnyOfItem,
): { href: string; label: string } => {
    if (attachment.type === 'file' && !attachment.url.startsWith('https://')) {
        return {
            href: `${process.env.REACT_APP_IMG_URL}${attachment.url}`,
            label: attachment?.fileName ?? attachment.url,
        };
    }
    return { href: attachment.url, label: attachment.fileName || attachment.url };
};

export function useDataProcessingAgreements({ enabled }: { enabled: boolean }): React.ReactNode {
    const user = useSelector((state: any) => state.user.data);
    const isAdmin = user?.role === 'ADMIN';
    const isDesktop = useMediaQuery('(min-width: 960px)');
    const [queue, setQueue] = useState<PendingAgreement[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const hasFetchedRef = useRef(false);

    const currentAgreement = queue[0] ?? null;
    const isOpen = Boolean(currentAgreement);

    const fetchPendingAgreements = useCallback(async () => {
        try {
            const response = await api.getApiDataProcessingAgreementsPending();
            const items = (response?.data ?? []) as PendingAgreement[];
            const eligible = items.filter(shouldShowAgreement);
            setQueue(eligible);
        } catch {
            // Pending agreements are non-blocking; avoid interrupting login flow
        }
    }, []);

    useEffect(() => {
        if (!enabled) {
            hasFetchedRef.current = false;
            setQueue([]);
            return;
        }
        if (!user) return;
        if (!isAdmin) {
            hasFetchedRef.current = false;
            setQueue([]);
            return;
        }
        if (hasFetchedRef.current) return;

        hasFetchedRef.current = true;
        fetchPendingAgreements();
    }, [enabled, isAdmin, user, fetchPendingAgreements]);

    const advanceQueue = useCallback(() => {
        setQueue((prev) => prev.slice(1));
    }, []);

    const acceptAgreement = useCallback(async (agreement: PendingAgreement) => {
        await api.postApiDataProcessingAgreementIdAccept(agreement.id, buildAcceptPayload(agreement));
    }, []);

    const handleButtonAction = useCallback(
        async (action: DpaButtonAction) => {
            if (!currentAgreement || isSubmitting) return;

            setIsSubmitting(true);
            try {
                if (action === 'accept') {
                    await acceptAgreement(currentAgreement);
                } else if (currentAgreement.isOneTime) {
                    await acceptAgreement(currentAgreement);
                } else {
                    writeShownRecord(currentAgreement.id);
                }
                advanceQueue();
            } catch {
                toast.error(t('Common.ToastWrong'));
            } finally {
                setIsSubmitting(false);
            }
        },
        [acceptAgreement, advanceQueue, currentAgreement, isSubmitting],
    );

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                void handleButtonAction(currentAgreement?.isOneTime ? 'accept' : 'close');
            }
        },
        [handleButtonAction, currentAgreement?.isOneTime],
    );

    if (!isAdmin || !currentAgreement) {
        return null;
    }

    const dialogButtons = getDialogButtons(currentAgreement);
    const buttonLayout = currentAgreement.additionalData?.buttonLayout ?? 'DOUBLE';
    const footerClassName =
        buttonLayout === 'SINGLE' ? 'flex w-full flex-col gap-2' : 'flex w-full justify-end gap-2';

    return (
        <RadixDialog
            open={isOpen}
            onOpenChange={handleOpenChange}
            position={isDesktop ? 'center' : 'bottom'}
            preventDismiss
            title={currentAgreement.title}
            className="max-w-2xl"
            contentClassName="!p-0"
            footer={
                <div className={footerClassName}>
                    {dialogButtons.map((button, index) => (
                        <RadixButton
                            key={`${button.action}-${index}`}
                            variant={button.action === 'accept' ? 'primary' : 'outline'}
                            disabled={isSubmitting}
                            className={buttonLayout === 'SINGLE' ? 'w-full' : undefined}
                            onClick={() => handleButtonAction(button.action)}
                        >
                            {button.label}
                        </RadixButton>
                    ))}
                </div>
            }
        >
            <div className="flex flex-col gap-4 px-1">
                <div
                    className="prose prose-sm max-w-none text-text-primary dark:text-text-primary"
                    dangerouslySetInnerHTML={{ __html: currentAgreement.content }}
                />
                {currentAgreement.attachments && currentAgreement.attachments.length > 0 && (
                    <div className="border-t border-border-default pt-4">
                        <p className="text-sm font-semibold text-text-primary m-0 mb-2">{t('Common.Attachments')}</p>
                        <ul className="flex flex-col gap-2 m-0 p-0 list-none">
                            {currentAgreement.attachments.map(
                                (
                                    attachment: GetApiDataProcessingAgreementsPending200DataItemAttachmentsAnyOfItem,
                                    index: number,
                                ) => {
                                    const { href, label } = getAttachmentHrefAndLabel(attachment);
                                    return (
                                        <li key={`${href}-${index}`}>
                                            <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm font-medium text-[#507fff] underline hover:text-[#4066cc] break-all"
                                            >
                                                {label}
                                            </a>
                                        </li>
                                    );
                                },
                            )}
                        </ul>
                    </div>
                )}
            </div>
        </RadixDialog>
    );
}
